import {RouterProps} from "../../RouterProps";
import {StatePair, useStatePair} from "react-type-extension";
import React, {useCallback} from "react";
import {Button, Title3} from "@fluentui/react-components";
import {
    PostBigIntField,
    PostDateField,
    PostFieldWrapper,
    PostFinishDialog
} from "../../PostField";
import {UserBlockReasonId} from "../../../data/id/user/UserBlockReasonId";
import {UserId} from "../../../data/id/user/UserId";
import {unwrapUndefinedDate} from "ts-type-extension";
import {UserBlockIO, UserBlockReasonIO, UserIO} from "../../../io/UserIO";
import {UserBlockDescription} from "../../../data/description/user/UserBlockDescription";
import {UserBlockReasonDomain} from "../../../data/domain/user/UserBlockReasonDomain";
import {UserBlockReasonSearchOption} from "../../../data/search-option/user/UserBlockReasonSearchOption";
import {searchIndex} from "../../../io/HttpClient";
import {ChevronLeft24Filled} from "@fluentui/react-icons";
import ListPageWrapper from "../../ListPageWrapper";
import {UserBlockReasonDescription} from "../../../data/description/user/UserBlockReasonDescription";
import {UserBlockReasonDescriptor} from "../../../data/descriptor/user/UserBlockReasonDescriptor";
import ListOption from "../../ListOption";
import {
    UserBlockReasonSearchOptionDescription
} from "../../../data/description/user/UserBlockReasonSearchOptionDescription";
import {
    applyUserBlockReasonSearchOption,
    UserBlockReasonSearchOptionField
} from "../../search-option-field/user/UserBlockReasonSearchOptionField";
import ListOrder from "../../ListOrder";
import {UserDomain} from "../../../data/domain/user/UserDomain";
import {UserSearchOption} from "../../../data/search-option/user/UserSearchOption";
import {UserDescription} from "../../../data/description/user/UserDescription";
import {UserDescriptor} from "../../../data/descriptor/user/UserDescriptor";
import {UserSearchOptionDescription} from "../../../data/description/user/UserSearchOptionDescription";
import {applyUserSearchOption, UserSearchOptionField} from "../../search-option-field/user/UserSearchOptionField";

enum Phase {
    Waiting,
    Posting,
    Finished,
    UserList,
    UserOption,
    UserOrder,
    UserBlockReasonList,
    UserBlockReasonOption,
    UserBlockReasonOrder,
}

export default function Post(props: RouterProps) {
    const phase = useStatePair<Phase>(Phase.Waiting)
    const userId = useStatePair<UserId>(0n)
    const userBlockReasonId = useStatePair<UserBlockReasonId>(0n)
    const blockedAt = useStatePair('')
    const blockedUntil = useStatePair<string | undefined>(undefined)

    const onDoneClick = useCallback(() => {
        phase.setter(Phase.Posting)
        UserBlockIO
            .post({
                userId: userId.value,
                userBlockReasonId: userBlockReasonId.value,
                blockedAt: new Date(blockedAt.value),
                blockedUntil: unwrapUndefinedDate(blockedUntil.value)
            })
            .then(() => phase.setter(Phase.Finished))
            .catch(reason => {
                console.error(reason)
                alert('추가를 실패했습니다.')
                phase.setter(Phase.Waiting)
            })
    }, [userId.value, userBlockReasonId.value, blockedAt.value, blockedUntil.value])

    return <>
        {(phase.value === Phase.Posting || phase.value === Phase.Waiting || phase.value === Phase.Finished) && <>
            <div className={props.styles.column16}>
                <Title3>새 항목</Title3>
                <PostFieldWrapper
                    {...props}
                    title={UserBlockDescription.userId}
                    showSearch
                    onSearchClick={() => phase.setter(Phase.UserList)}>
                    <PostBigIntField
                        {...props}
                        value={userId.value}
                        setter={userId.setter}/>
                </PostFieldWrapper>
                <PostFieldWrapper
                    {...props}
                    title={UserBlockDescription.userBlockReasonId}
                    showSearch
                    onSearchClick={() => phase.setter(Phase.UserBlockReasonList)}>
                    <PostBigIntField
                        {...props}
                        value={userBlockReasonId.value}
                        setter={userBlockReasonId.setter}/>
                </PostFieldWrapper>
                <PostFieldWrapper
                    {...props}
                    title={UserBlockDescription.blockedAt}>
                    <PostDateField
                        {...props}
                        value={blockedAt.value}
                        setter={blockedAt.setter}
                        maxLength={19} />
                </PostFieldWrapper>
                <PostFieldWrapper
                    {...props}
                    title={UserBlockDescription.blockedUntil}
                    showUnusedCheckbox
                    isUnusedCheckboxChecked={blockedUntil.value === undefined}
                    onUnusedCheckboxChange={checked => blockedUntil.setter(checked ? undefined : '')}>
                    <PostDateField
                        {...props}
                        disabled={blockedUntil.value === undefined}
                        value={blockedUntil.value}
                        setter={blockedUntil.setter}
                        maxLength={19} />
                </PostFieldWrapper>
                <div className={props.styles.row}>
                    <Button
                        appearance={'primary'}
                        disabled={phase.value !== Phase.Waiting}
                        onClick={onDoneClick}>
                        완료
                    </Button>
                </div>
            </div>
        </>}
        <UserSearch
            {...props}
            phase={phase}
            userId={userId} />
        <UserBlockReasonSearch
            {...props}
            phase={phase}
            userBlockReasonId={userBlockReasonId} />
        <PostFinishDialog
            open={phase.value === Phase.Finished}
            negativeHref={'/users/blocks'}/>
    </>
}



type UserSearchProps = RouterProps & {
    phase: StatePair<Phase>
    userId: StatePair<UserId>
}

function UserSearch(props: UserSearchProps) {
    const items = useStatePair<UserDomain[]>([])
    const option = useStatePair<UserSearchOption>({})
    const index = useStatePair(searchIndex())

    return <>
        {props.phase.value === Phase.UserList && <>
            <div className={props.styles.column16}>
                <div className={props.styles.row8}>
                    <Button
                        appearance={'subtle'}
                        icon={<ChevronLeft24Filled/>}
                        onClick={() => props.phase.setter(Phase.Waiting)}>
                    </Button>
                    <Title3>사용자 검색</Title3>
                </div>
                <ListPageWrapper
                    {...props}
                    client={UserIO}
                    items={items}
                    option={option.value}
                    index={index}
                    description={UserDescription}
                    descriptor={UserDescriptor}
                    showFilter
                    onFilterClick={() => props.phase.setter(Phase.UserOption)}
                    showSort
                    onSortClick={() => props.phase.setter(Phase.UserOrder)}
                    onItemClick={item => {
                        props.userId.setter(item.id)
                        props.phase.setter(Phase.Waiting)
                    }}/>
            </div>
        </>}
        {props.phase.value === Phase.UserOption && <>
            <div className={props.styles.column16}>
                <div className={props.styles.row8}>
                    <Button
                        appearance={'subtle'}
                        icon={<ChevronLeft24Filled/>}
                        onClick={() => props.phase.setter(Phase.Waiting)} />
                    <Title3>사용자 검색</Title3>
                </div>
                <ListOption
                    {...props}
                    option={option.value}
                    description={UserSearchOptionDescription}
                    fields={UserSearchOptionField}
                    onBackClick={() => props.phase.setter(Phase.UserList)}
                    onApplyClick={(key, value) => {
                        applyUserSearchOption(key, value, option)
                        index.setter({...index.value, pageIndex: 0n})
                        items.setter([])
                    }}
                    onDismissClick={key => {
                        const next = {...option.value}
                        next[key] = undefined
                        option.setter(next)
                        items.setter([])
                    }}/>
            </div>
        </>}
        {props.phase.value === Phase.UserOrder && <>
            <ListOrder
                {...props}
                index={index}
                description={UserDescription}
                onBackClick={() => props.phase.setter(Phase.UserList)}
                onOrderUpdated={() => items.setter([])} />
        </>}
    </>
}

type UserBlockReasonSearchProps = RouterProps & {
    phase: StatePair<Phase>
    userBlockReasonId: StatePair<UserBlockReasonId>
}

function UserBlockReasonSearch(props: UserBlockReasonSearchProps) {
    const items = useStatePair<UserBlockReasonDomain[]>([])
    const option = useStatePair<UserBlockReasonSearchOption>({})
    const index = useStatePair(searchIndex())

    return <>
        {props.phase.value === Phase.UserBlockReasonList && <>
            <div className={props.styles.column16}>
                <div className={props.styles.row8}>
                    <Button
                        appearance={'subtle'}
                        icon={<ChevronLeft24Filled/>}
                        onClick={() => props.phase.setter(Phase.Waiting)}>
                    </Button>
                    <Title3>사용자 이용 정지 사유 검색</Title3>
                </div>
                <ListPageWrapper
                    {...props}
                    client={UserBlockReasonIO}
                    items={items}
                    option={option.value}
                    index={index}
                    description={UserBlockReasonDescription}
                    descriptor={UserBlockReasonDescriptor}
                    showFilter
                    onFilterClick={() => props.phase.setter(Phase.UserBlockReasonOption)}
                    showSort
                    onSortClick={() => props.phase.setter(Phase.UserBlockReasonOrder)}
                    onItemClick={item => {
                        props.userBlockReasonId.setter(item.id)
                        props.phase.setter(Phase.Waiting)
                    }}/>
            </div>
        </>}
        {props.phase.value === Phase.UserBlockReasonOption && <>
            <div className={props.styles.column16}>
                <div className={props.styles.row8}>
                    <Button
                        appearance={'subtle'}
                        icon={<ChevronLeft24Filled/>}
                        onClick={() => props.phase.setter(Phase.Waiting)} />
                    <Title3>사용자 이용 정지 사유 검색</Title3>
                </div>
                <ListOption
                    {...props}
                    option={option.value}
                    description={UserBlockReasonSearchOptionDescription}
                    fields={UserBlockReasonSearchOptionField}
                    onBackClick={() => props.phase.setter(Phase.UserBlockReasonList)}
                    onApplyClick={(key, value) => {
                        applyUserBlockReasonSearchOption(key, value, option)
                        index.setter({...index.value, pageIndex: 0n})
                        items.setter([])
                    }}
                    onDismissClick={key => {
                        const next = {...option.value}
                        next[key] = undefined
                        option.setter(next)
                        items.setter([])
                    }}/>
            </div>
        </>}
        {props.phase.value === Phase.UserBlockReasonOrder && <>
            <ListOrder
                {...props}
                index={index}
                description={UserBlockReasonDescription}
                onBackClick={() => props.phase.setter(Phase.UserBlockReasonList)}
                onOrderUpdated={() => items.setter([])} />
        </>}
    </>
}